import {faChevronDown} from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import {faChevronUp} from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import {faHighlighterLine} from '@fortawesome/pro-regular-svg-icons/faHighlighterLine';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import ButtonBase, {buttonBaseClasses} from '@mui/material/ButtonBase';
import {listClasses} from '@mui/material/List';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import {varAlpha} from 'minimal-shared/utils';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import type {EditorToolbarProps} from '@minimal/components/editor';
import {ToolbarItem} from '@minimal/components/editor/components/toolbar-item';

import {MeaInfoEnum, MeaInfoValues} from './mea-info-types';

export const MeaInfoToolbar = ({editor}: Pick<EditorToolbarProps, 'editor'>) => {
  const {t} = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!editor) {
    return null;
  }

  return (
    <>
      <Tooltip title={t('components.editor.toolbar.mea')} placement="top">
        <ButtonBase
          id="heading-menu-button"
          aria-label="Heading menu button"
          aria-controls={anchorEl ? 'heading-menu-button' : undefined}
          aria-haspopup="true"
          aria-expanded={anchorEl ? 'true' : undefined}
          onClick={handleClick}
          sx={(theme) => ({
            px: 1,
            width: 160,
            height: 32,
            borderRadius: 0.75,
            gap: 1,
            typography: 'body2',
            justifyContent: 'space-between',
            border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`,
          })}
        >
          <FontAwesomeIcon icon={faHighlighterLine} />
          {(editor.isActive('meaInfo', {type: MeaInfoEnum.warning}) &&
            t(`components.editor.mea-info.${MeaInfoEnum.warning}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.example}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.example}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.note}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.note}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.important}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.important}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.advice}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.advice}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.good_to_know}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.good_to_know}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.reminder}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.reminder}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.illustration}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.illustration}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.old}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.old}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.expert_advice}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.expert_advice}`)) ||
            (editor.isActive('meaInfo', {type: MeaInfoEnum.default}) &&
              t(`components.editor.mea-info.${MeaInfoEnum.default}`)) ||
            t('components.editor.toolbar.mea')}

          <FontAwesomeIcon icon={anchorEl ? faChevronUp : faChevronDown} />
        </ButtonBase>
      </Tooltip>

      <Menu
        id="heading-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        MenuListProps={{'aria-labelledby': 'heading-button'}}
        slotProps={{
          paper: {
            sx: {
              width: 120,
              [`& .${listClasses.root}`]: {gap: 0.5, display: 'flex', flexDirection: 'column'},
              [`& .${buttonBaseClasses.root}`]: {
                px: 1,
                width: 1,
                height: 34,
                borderRadius: 0.75,
                justifyContent: 'flex-start',
                '&:hover': {backgroundColor: 'action.hover'},
              },
            },
          },
        }}
      >
        <ToolbarItem
          component="li"
          label={t('components.editor.heading.paragraph')}
          active={editor.isActive('paragraph')}
          onClick={() => {
            handleClose();
            editor.chain().focus().setParagraph().run();
          }}
        />

        {MeaInfoValues.map((meaInfo) => {
          const active = editor.isActive('meaInfo', {type: meaInfo});
          return (
            <ToolbarItem
              aria-label={meaInfo}
              component="li"
              key={meaInfo}
              label={t(`components.editor.mea-info.${meaInfo}`)}
              active={active}
              onClick={() => {
                handleClose();
                if (active) {
                  editor.chain().focus().unsetMeaInfoBlock().run();
                  return;
                }
                editor.chain().focus().setMeaInfoBlock(meaInfo).run();
              }}
            />
          );
        })}
      </Menu>
    </>
  );
};
